import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Alert,
  Switch,
  ScrollView,
} from 'react-native';

interface Business {
  id: string;
  name: string;
  category: string;
  rating: number;
  distance: string;
  address: string;
  phone: string;
  imageUrl?: string;
  isFavorite: boolean;
  services: string[];
  priceRange: string;
  hours: string;
  paymentRequiredAtBooking?: boolean;
}

interface BusinessBookingModalProps {
  visible: boolean;
  business: Business | null;
  onClose: () => void;
  onBookNow: (options: BookingOptions) => void;
  onAddToFavorites: (businessId: string) => void;
  onRemoveFromFavorites: (businessId: string) => void;
}

interface BookingOptions {
  isRecurring: boolean;
  recurringFrequency: 'weekly' | 'biweekly' | 'monthly' | 'yearly';
  addToCalendar: boolean;
  paymentMethod: 'immediate' | 'pay_later';
}

const BusinessBookingModal: React.FC<BusinessBookingModalProps> = ({
  visible,
  business,
  onClose,
  onBookNow,
  onAddToFavorites,
  onRemoveFromFavorites,
}) => {
  const [bookingOptions, setBookingOptions] = useState<BookingOptions>({
    isRecurring: false,
    recurringFrequency: 'weekly',
    addToCalendar: true,
    paymentMethod: 'immediate',
  });

  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    if (business) {
      setIsFavorite(business.isFavorite);
      // If business requires payment at booking, force pay-now and hide pay-later choice
      if (business.paymentRequiredAtBooking) {
        setBookingOptions(prev => ({ ...prev, paymentMethod: 'immediate' }));
      }
    }
  }, [business]);

  if (!business) return null;

  const handleFavoriteToggle = () => {
    if (isFavorite) {
      onRemoveFromFavorites(business.id);
      setIsFavorite(false);
      Alert.alert('Removed from Favorites', `${business.name} has been removed from your favorites.`);
    } else {
      onAddToFavorites(business.id);
      setIsFavorite(true);
      Alert.alert('Added to Favorites', `${business.name} has been added to your favorites!`);
    }
  };

  const handleBookNow = () => {
    onBookNow(bookingOptions);
    onClose();
  };

  const updateBookingOption = (key: keyof BookingOptions, value: any) => {
    setBookingOptions(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push('★');
    }
    if (hasHalfStar) {
      stars.push('☆');
    }
    while (stars.length < 5) {
      stars.push('☆');
    }

    return stars.join('');
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Book Appointment</Text>
          <View style={{ width: 30 }} />
        </View>

        <ScrollView style={styles.content}>
          {/* Business Info */}
          <View style={styles.businessCard}>
            <View style={styles.businessHeader}>
              <View style={styles.businessInfo}>
                <Text style={styles.businessName}>{business.name}</Text>
                <Text style={styles.businessCategory}>{business.category}</Text>
                <View style={styles.businessRating}>
                  <Text style={styles.ratingText}>{renderStars(business.rating)}</Text>
                  <Text style={styles.ratingNumber}>({business.rating})</Text>
                </View>
              </View>
              <TouchableOpacity
                style={[styles.favoriteButton, isFavorite && styles.favoriteButtonActive]}
                onPress={handleFavoriteToggle}
              >
                <Text style={[styles.favoriteIcon, isFavorite && styles.favoriteIconActive]}>
                  {isFavorite ? '♥' : '♡'}
                </Text>
              </TouchableOpacity>
            </View>
            
            <Text style={styles.businessAddress}>{business.address}</Text>
            <Text style={styles.businessDistance}>{business.distance} away</Text>
            <Text style={styles.businessHours}>Hours: {business.hours}</Text>
          </View>

          {/* Booking Options */}
          <View style={styles.optionsContainer}>
            <Text style={styles.optionsTitle}>Booking Options</Text>

            {/* Recurring Appointment */}
            <View style={styles.optionItem}>
              <View style={styles.optionInfo}>
                <Text style={styles.optionLabel}>🔄 Recurring Appointment</Text>
                <Text style={styles.optionDescription}>
                  Set up automatic repeat bookings
                </Text>
              </View>
              <Switch
                value={bookingOptions.isRecurring}
                onValueChange={(value) => updateBookingOption('isRecurring', value)}
                trackColor={{ false: '#E5E5E7', true: '#007AFF' }}
                thumbColor={bookingOptions.isRecurring ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Recurring Frequency */}
            {bookingOptions.isRecurring && (
              <View style={styles.frequencyContainer}>
                <Text style={styles.frequencyLabel}>Repeat every:</Text>
                <View style={styles.frequencyOptions}>
                  {[
                    { key: 'weekly', label: 'Week' },
                    { key: 'biweekly', label: '2 Weeks' },
                    { key: 'monthly', label: 'Month' },
                    { key: 'yearly', label: 'Year' },
                  ].map((option) => (
                    <TouchableOpacity
                      key={option.key}
                      style={[
                        styles.frequencyOption,
                        bookingOptions.recurringFrequency === option.key && styles.frequencyOptionActive,
                      ]}
                      onPress={() => updateBookingOption('recurringFrequency', option.key)}
                    >
                      <Text
                        style={[
                          styles.frequencyOptionText,
                          bookingOptions.recurringFrequency === option.key && styles.frequencyOptionTextActive,
                        ]}
                      >
                        {option.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}

            {/* Add to Calendar */}
            <View style={styles.optionItem}>
              <View style={styles.optionInfo}>
                <Text style={styles.optionLabel}>📅 Add to Calendar</Text>
                <Text style={styles.optionDescription}>
                  Sync with your calendar app
                </Text>
              </View>
              <Switch
                value={bookingOptions.addToCalendar}
                onValueChange={(value) => updateBookingOption('addToCalendar', value)}
                trackColor={{ false: '#E5E5E7', true: '#007AFF' }}
                thumbColor={bookingOptions.addToCalendar ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Payment Method */}
            <View style={styles.optionItem}>
              <View style={styles.optionInfo}>
                <Text style={styles.optionLabel}>💳 Payment Method</Text>
                <Text style={styles.optionDescription}>
                  Choose when to pay
                </Text>
              </View>
            </View>

            <View style={styles.paymentOptions}>
              <TouchableOpacity
                style={[
                  styles.paymentOption,
                  bookingOptions.paymentMethod === 'immediate' && styles.paymentOptionActive,
                ]}
                onPress={() => updateBookingOption('paymentMethod', 'immediate')}
              >
                <Text style={styles.paymentOptionIcon}>💳</Text>
                <Text
                  style={[
                    styles.paymentOptionText,
                    bookingOptions.paymentMethod === 'immediate' && styles.paymentOptionTextActive,
                  ]}
                >
                  Pay Now
                </Text>
              </TouchableOpacity>

              {!business.paymentRequiredAtBooking && (
                <TouchableOpacity
                  style={[
                    styles.paymentOption,
                    bookingOptions.paymentMethod === 'pay_later' && styles.paymentOptionActive,
                  ]}
                  onPress={() => updateBookingOption('paymentMethod', 'pay_later')}
                >
                  <Text style={styles.paymentOptionIcon}>⏰</Text>
                  <Text
                    style={[
                      styles.paymentOptionText,
                      bookingOptions.paymentMethod === 'pay_later' && styles.paymentOptionTextActive,
                    ]}
                  >
                    Confirm booking
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* Services Preview */}
          <View style={styles.servicesContainer}>
            <Text style={styles.servicesTitle}>Available Services</Text>
            <View style={styles.servicesList}>
              {business.services.slice(0, 4).map((service, index) => (
                <View key={index} style={styles.serviceItem}>
                  <Text style={styles.serviceText}>• {service}</Text>
                </View>
              ))}
              {business.services.length > 4 && (
                <Text style={styles.moreServicesText}>+ {business.services.length - 4} more services</Text>
              )}
            </View>
          </View>
        </ScrollView>

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.bookButton} onPress={handleBookNow}>
            <Text style={styles.bookButtonText}>
              {bookingOptions.paymentMethod === 'immediate' ? 'Book & Pay Now' : 'Confirm booking'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    paddingTop: 50,
    backgroundColor: '#007AFF',
  },
  closeButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  businessCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  businessHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  businessInfo: {
    flex: 1,
    marginRight: 10,
  },
  businessName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  businessCategory: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '600',
    marginBottom: 8,
  },
  businessRating: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  ratingText: {
    fontSize: 14,
    color: '#FF9500',
    marginRight: 4,
  },
  ratingNumber: {
    fontSize: 14,
    color: '#666',
  },
  favoriteButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F2F2F7',
    justifyContent: 'center',
    alignItems: 'center',
  },
  favoriteButtonActive: {
    backgroundColor: '#FF3B30',
  },
  favoriteIcon: {
    fontSize: 20,
    color: '#8E8E93',
  },
  favoriteIconActive: {
    color: 'white',
  },
  businessAddress: {
    fontSize: 14,
    color: '#666',
    marginBottom: 4,
  },
  businessDistance: {
    fontSize: 12,
    color: '#8E8E93',
    marginBottom: 4,
  },
  businessHours: {
    fontSize: 12,
    color: '#8E8E93',
  },
  optionsContainer: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  optionsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 16,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F8F9FA',
  },
  optionInfo: {
    flex: 1,
    marginRight: 10,
  },
  optionLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  optionDescription: {
    fontSize: 14,
    color: '#666',
  },
  frequencyContainer: {
    marginTop: 8,
    marginBottom: 8,
  },
  frequencyLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  frequencyOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  frequencyOption: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#F2F2F7',
    borderWidth: 1,
    borderColor: 'transparent',
  },
  frequencyOptionActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  frequencyOptionText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#666',
  },
  frequencyOptionTextActive: {
    color: 'white',
  },
  paymentOptions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  paymentOption: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: '#F2F2F7',
    borderWidth: 1,
    borderColor: 'transparent',
  },
  paymentOptionActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  paymentOptionIcon: {
    fontSize: 16,
    marginRight: 6,
  },
  paymentOptionText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
  },
  paymentOptionTextActive: {
    color: 'white',
  },
  servicesContainer: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  servicesTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 12,
  },
  servicesList: {
    gap: 4,
  },
  serviceItem: {
    paddingVertical: 2,
  },
  serviceText: {
    fontSize: 14,
    color: '#666',
  },
  moreServicesText: {
    fontSize: 12,
    color: '#8E8E93',
    fontStyle: 'italic',
    marginTop: 4,
  },
  actionButtons: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
    backgroundColor: 'white',
    borderTopWidth: 1,
    borderTopColor: '#E5E5E7',
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: 8,
    backgroundColor: '#F2F2F7',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  bookButton: {
    flex: 2,
    paddingVertical: 16,
    borderRadius: 8,
    backgroundColor: '#007AFF',
    alignItems: 'center',
  },
  bookButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
});

export default BusinessBookingModal;



